import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import apiClient from '../../../core/http/axios';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  pointer-events: none;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;

const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;
  
  html.dark-mode & img {
    filter: invert(1);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing['4xl']};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: 141%;
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const Form = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing.xl};
  pointer-events: auto;
`;

const FormField = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const LabelText = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
  & > .required {
    color: #DF0707;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
  width: 100%;
  &.row {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.white};
  background-color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  transition: all 0.2s;
  height: 44px;
  width: 100%;
  &:focus {
    outline: none;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${props => props.theme.colors.white} inset !important;
    -webkit-text-fill-color: ${props => props.theme.colors.book.color2b} !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }

`;

const CodeButton = styled.button`
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.book.color3b};
  color: #fff;
  font-size: ${props => props.theme.fontSize.base};
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  width: 100%;

  &:disabled {
    opacity: 0.8;
    background-color: ${props => props.theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[500]};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${props => props.theme.colors.gray[700]};
  }
`;

const ErrorMessage = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.error[500]};
  margin-top: ${props => props.theme.spacing.xs};
`;

const ResetPasswordNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetToken = (location.state as any)?.resetToken;

  useEffect(() => {
    if (!resetToken) {
      navigate('/reset-password');
    }
  }, [resetToken, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (formData.password.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    if (!resetToken) {
      setError('인증 토큰이 없습니다. 다시 시도해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: null;
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/reset-password/confirm', {
        reset_token: resetToken,
        new_password: formData.password,
        new_password_confirm: formData.confirmPassword,
      });

      if (response.data.success) {
        navigate('/reset-password/complete');
      } else {
        setError(response.data.error || '비밀번호 재설정에 실패했습니다.');
      }
    } catch (error: any) {
      // console.error('비밀번호 재설정 오류:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '비밀번호 재설정 중 오류가 발생했습니다.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.password.length >= 8 &&
      formData.confirmPassword.length >= 8 &&
      formData.password === formData.confirmPassword
    );
  };

  return (
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Content>
        <Title>비밀번호 재설정</Title>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <LabelText htmlFor="password">
              비밀번호 <span className="required">*</span>
            </LabelText>
            <Input
              type="password"
              placeholder="비밀번호 재설정"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              $hasError={false}
            />
          </FormField>

          <FormField>
            <LabelText htmlFor="confirmPassword">
              비밀번호 재입력 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="password"
                placeholder="비밀번호 재입력"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                $hasError={formData.confirmPassword !== formData.password && formData.confirmPassword.length > 0}
              />
            </InputGroup>
          </FormField>

          <CodeButton type="submit" disabled={!isFormValid() || isSubmitting}>
            {isSubmitting ? '처리 중...' : '수정하기'}
          </CodeButton>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          <CodeButton type="button" onClick={() => navigate('/')}>
            메인페이지로 이동
          </CodeButton>
        </Form>
      </Content>
    </Container>
  );
};

export default ResetPasswordNew;
